import { useState, useEffect, useCallback } from 'react';
import Navbar from '../components/Navbar';
import { supabase } from '../lib/supabase';
import { validateProperty } from '../lib/validation';
import { logPropertyCreated, logPropertyUpdated, logPropertyDeleted } from '../lib/auditLog';
import { escapeIlike } from '../lib/searchUtils';

const emptyProperty = {
  name: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  property_manager_name: '',
  property_manager_phone: '',
  property_email: '',
  billing_email: '',
  gate_code: '',
  access_notes: '',
};

function Properties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [formData, setFormData] = useState(emptyProperty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [nameConflict, setNameConflict] = useState(false);

  const PROPERTY_COLUMNS = 'id, name, address, city, state, zip, property_manager_name, property_manager_phone, property_email, billing_email, gate_code, access_notes';

  const fetchProperties = useCallback(async () => {
    const { data, error } = await supabase
      .from('properties')
      .select(PROPERTY_COLUMNS)
      .eq('is_deleted', false)
      .order('name')
      .limit(200);

    if (error) {
      setError('Failed to load properties');
    } else {
      setProperties(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    let mounted = true;
    fetchProperties().finally(() => { if (!mounted) return; });
    return () => { mounted = false; };
  }, [fetchProperties]);

  // Debounced duplicate-name check (400ms)
  useEffect(() => {
    if (!formData.name.trim() || !showForm) {
      setNameConflict(false);
      return;
    }
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from('properties')
        .select('id')
        .eq('is_deleted', false)
        .ilike('name', escapeIlike(formData.name.trim()))
        .limit(1);

      if (data && data.length > 0) {
        // When editing, exclude the current record
        const isConflict = editingProperty
          ? data.some((p) => p.id !== editingProperty.id)
          : true;
        setNameConflict(isConflict);
      } else {
        setNameConflict(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [formData.name, editingProperty, showForm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate input
    const validation = validateProperty(formData);
    if (!validation.isValid) {
      setError(Object.values(validation.errors)[0]);
      return;
    }

    if (nameConflict) {
      setError('A property with this name already exists.');
      return;
    }

    setSaving(true);

    if (editingProperty) {
      const { error } = await supabase
        .from('properties')
        .update(formData)
        .eq('id', editingProperty.id);

      if (error) {
        setError(error.code === '23505' ? 'A property with this name already exists.' : 'Failed to update property');
        setSaving(false);
        return;
      }

      logPropertyUpdated(editingProperty.id, {
        name: formData.name,
        address: formData.address,
      });
    } else {
      const { data, error } = await supabase
        .from('properties')
        .insert([formData])
        .select('id')
        .single();

      if (error) {
        setError(error.code === '23505' ? 'A property with this name already exists.' : 'Failed to create property');
        setSaving(false);
        return;
      }

      logPropertyCreated(data.id, {
        name: formData.name,
        address: formData.address,
      });
    }

    setSaving(false);
    setShowForm(false);
    setEditingProperty(null);
    setFormData(emptyProperty);
    fetchProperties();
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setFormData({
      name: property.name || '',
      address: property.address || '',
      city: property.city || '',
      state: property.state || '',
      zip: property.zip || '',
      property_manager_name: property.property_manager_name || '',
      property_manager_phone: property.property_manager_phone || '',
      property_email: property.property_email || '',
      billing_email: property.billing_email || '',
      gate_code: property.gate_code || '',
      access_notes: property.access_notes || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this property? Associated jobs will also be hidden.')) {
      return;
    }

    const propertyToDelete = properties.find(p => p.id === id);

    const { error } = await supabase
      .from('properties')
      .update({ is_deleted: true })
      .eq('id', id);

    if (error) {
      setError('Failed to delete property');
    } else {
      logPropertyDeleted(id, {
        name: propertyToDelete?.name,
      });
      fetchProperties();
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingProperty(null);
    setFormData(emptyProperty);
    setError('');
    setNameConflict(false);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Properties</h1>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="bg-brand-coral text-white px-3 py-2 text-sm sm:px-4 sm:text-base rounded-md hover:bg-brand-coral-dark"
            >
              Add Property
            </button>
          )}
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4">
            {error}
          </div>
        )}

        {showForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">
              {editingProperty ? 'Edit Property' : 'Add New Property'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Property Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      nameConflict ? 'border-amber-400' : 'border-gray-300'
                    }`}
                  />
                  {nameConflict && (
                    <p className="text-sm text-amber-600 mt-1">
                      A property with this name already exists.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address *
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ZIP
                    </label>
                    <input
                      type="text"
                      name="zip"
                      value={formData.zip}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Property Manager Name and Phone #
                  </label>
                  <input
                    type="text"
                    name="property_manager_name"
                    value={formData.property_manager_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maintenance Supervisor Name and Phone
                  </label>
                  <input
                    type="text"
                    name="property_manager_phone"
                    value={formData.property_manager_phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Property Email
                  </label>
                  <input
                    type="email"
                    name="property_email"
                    value={formData.property_email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Billing Email
                  </label>
                  <input
                    type="email"
                    name="billing_email"
                    value={formData.billing_email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gate Code
                  </label>
                  <input
                    type="text"
                    name="gate_code"
                    value={formData.gate_code}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Access Notes
                  </label>
                  <textarea
                    name="access_notes"
                    value={formData.access_notes}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || nameConflict}
                  className="px-4 py-2 bg-brand-coral text-white rounded-md hover:bg-brand-coral-dark disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingProperty ? 'Update Property' : 'Add Property'}
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading properties...</div>
        ) : properties.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-gray-500">No properties yet. Add your first property to get started.</p>
          </div>
        ) : (
          <>
            {/* Mobile Card Layout */}
            <div className="lg:hidden space-y-3">
              {properties.map((property) => (
                <div key={property.id} className="bg-white rounded-lg shadow-md p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 truncate">
                        {property.name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {property.address}
                      </p>
                      <p className="text-sm text-gray-500">
                        {[property.city, property.state, property.zip].filter(Boolean).join(', ')}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 ml-2">
                      <button
                        onClick={() => handleEdit(property)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
                        title="Edit"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(property.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                        title="Delete"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-2 text-sm">
                    {property.property_manager_name && (
                      <div>
                        <span className="text-gray-500">Manager:</span>
                        <p className="text-gray-900 truncate">{property.property_manager_name}</p>
                      </div>
                    )}
                    {property.property_manager_phone && (
                      <div>
                        <span className="text-gray-500">Maintenance:</span>
                        <p className="text-gray-900 truncate">{property.property_manager_phone}</p>
                      </div>
                    )}
                    {property.gate_code && (
                      <div>
                        <span className="text-gray-500">Gate:</span>
                        <p className="text-gray-900">{property.gate_code}</p>
                      </div>
                    )}
                    {property.property_email && (
                      <div>
                        <span className="text-gray-500">Email:</span>
                        <p className="text-gray-900 truncate">{property.property_email}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table Layout */}
            <div className="hidden lg:block bg-white rounded-lg shadow-md overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Property
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Manager
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gate Code
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {properties.map((property) => (
                    <tr key={property.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{property.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{property.address}</div>
                        <div className="text-sm text-gray-500">
                          {[property.city, property.state, property.zip].filter(Boolean).join(', ')}
                        </div>
                      </td>
                      <td className="px-6 py-4 max-w-xs">
                        <div className="text-sm text-gray-900 break-words">{property.property_manager_name || '-'}</div>
                        <div className="text-sm text-gray-500 break-words">{property.property_manager_phone || ''}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {property.gate_code || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(property)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(property.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default Properties;
